import * as React from "react";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useAuth0 } from "@auth0/auth0-react";
import { reportAuthFailure } from "../utils/sentry";
import { persistAuthError } from "../utils/authErrors";

export default function AccessDenied() {
  const { logout, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const reason =
    location.state?.reason ||
    "You do not have permission to access this application. Admin privileges are required.";
  const reportedRef = useRef(null);

  useEffect(() => {
    if (reportedRef.current === reason) return;
    reportedRef.current = reason;
    persistAuthError(reason);
    reportAuthFailure("access_denied", reason);
  }, [reason]);

  const handleSignOut = () => {
    persistAuthError(reason);
    if (isAuthenticated) {
      logout({
        logoutParams: {
          returnTo: window.location.origin + "/sign-in",
        },
      });
      return;
    }
    navigate("/sign-in", { replace: true });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: (theme) =>
          theme.palette.mode === "light"
            ? theme.palette.grey[100]
            : theme.palette.grey[900],
        p: 3,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <LockIcon
            sx={{
              fontSize: 120,
              color: "error.main",
              mb: 2,
            }}
          />
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            Access Denied
          </Typography>
          <Alert severity="error" sx={{ width: "100%", mb: 3, textAlign: "left" }}>
            <Typography variant="body1">{reason}</Typography>
          </Alert>
          <Button variant="contained" onClick={handleSignOut} sx={{ mb: 1 }}>
            {isAuthenticated ? "Sign out and try again" : "Back to sign in"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            Share the message above with support if you need help.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
